import { translate, updateUIFromShadowRoot } from '@core/i18n/window-language';
import { loadVideoWithPartialContentSupport } from '@core/utils/api';
import { getStoreConfiguration, getStoreValue } from '@core/utils/store';
import { cleanTextToCards } from '@core/utils/utils';
import { COMMON_EXCLUSIVE_WATERMARK_IMG } from '@core/constants/WATERMARK';
import {
    IMAGE_NOT_FOUND,
    ADD_CART_ELEMENT_TYPE,
    MAX_FLEX_GROW_VALUE,
} from '@core/constants/CONSTANTS';
import { DEFAULT_LANGUAGE, DEFAULT_ZOOM_VALUE } from '@core/constants/DEFAULT';
import { isVideoItem } from '@core/utils/active';
import { isReportVideosList, isCreativeReportType } from '@core/utils/report';
import { formatCardDate } from '@core/utils/date';
import {
    WESOG_STORE_CONFIGURATION_PARAMS,
    WESOG_STORE_PARAMS,
} from '@core/constants/STORE';
import {
    ACTIVE_ID_ATTRIBUTE,
    ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
    CUSTOM_FLEX_GROW_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
    ITEM_NAME_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import { SERVICE_WORKER_VERSION } from '@core/constants/SERVICE-WORKER';
import {
    REMOVE_ACTIVE_ITEM,
    LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
    LIGHTBOX_MODAL_ATTRIBUTES,
    ELEMENT_TYPE_ADD_LIGHTBOX,
} from '@core/constants/LIGHTBOX';
import { COMMON_REPORTS, VIDEO_REPORTS } from '@core/constants/REPORTS';
import {
    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
    TOOLTIP_POSITION_ATTRIBUTE,
    BOUND_POSITION_ATTRIBUTE,
    BOUND_POSITIONS,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

import { CUSTOM_REPORT_CARD_LOADING_SPINNER_WEB_COMPONENT } from './CONSTANTS';

/**
 * Handle translation helper for the component.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {string} currentLanguage - The current language.
 * @param {string} defaultLanguage - The default language.
 * @returns {void}
 */
export function handleTranslationHelper(shadowRoot, currentLanguage, defaultLanguage) {
    updateUIFromShadowRoot(shadowRoot);

    const exclusiveImg = shadowRoot.querySelector(`.${COMMON_EXCLUSIVE_WATERMARK_IMG}`);
    if (exclusiveImg) {
        exclusiveImg.src = `assets/img/exclusive_watermark_${currentLanguage || defaultLanguage}.svg`;
    }
}

/**
 * Update the date format in the card.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {string} date - The date to format.
 * @returns {void}
 */
export function updateFormatCardDate(shadowRoot, date) {
    const titleElement = shadowRoot.querySelector('h3');
    if (titleElement) {
        titleElement.innerText = formatCardDate(date);
    }
}

/**
 * Setup container styles based on image dimensions.
 * @param {HTMLDivElement} container - The main container element.
 * @param {Object} img - The image data.
 * @param {number} minImgHeight - The minimum image height.
 * @returns {Object} The calculated flex grow and width values.
 */
export function setupContainerStyles(container, img, minImgHeight) {
    const customFlexGrow =
        img.img_width !== 'auto' ? (img.img_width * minImgHeight) / img.img_height : 0;
    const customWidth =
        img.img_width !== 'auto'
            ? `${(img.img_width * minImgHeight) / img.img_height}px`
            : 'auto';

    container.style.width = customWidth;
    container.style.flexGrow = customFlexGrow;
    container.style.minHeight = `${minImgHeight}px`;

    return { customFlexGrow, customWidth };
}

/**
 * Setup image element with proper source and alt text.
 * @param {HTMLImageElement} imgElement - The image element.
 * @param {Object} img - The image data.
 * @param {string} reportTitle - The report title.
 * @returns {void}
 */
export function setupImageElement(imgElement, img, reportTitle) {
    imgElement.src = img.img_src || IMAGE_NOT_FOUND;
    imgElement.alt = reportTitle;
}

/**
 * Setup description elements with title and caption.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {string} reportTitle - The report title.
 * @param {Object} img - The image data.
 * @param {string} reportUid - The report UID.
 * @returns {void}
 */
export function setupDescriptionElements(shadowRoot, reportTitle, img, reportUid) {
    const paragraphs = shadowRoot.querySelectorAll(
        '.img-title-description-report-card p',
    );

    if (paragraphs[0]) {
        paragraphs[0].innerText = reportTitle;
        paragraphs[0].style.viewTransitionName = `report-title-${reportUid}`;
    }

    if (paragraphs[1]) {
        paragraphs[1].innerText = cleanTextToCards(img.caption || '');
    }
}

/**
 * Setup photos count display.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {number} numberPhotos - The number of photos.
 * @returns {void}
 */
export function setupPhotosCount(shadowRoot, numberPhotos) {
    const span = shadowRoot.querySelector('.img-icons > span');
    if (span) {
        span.innerText = numberPhotos;
    }
}

/**
 * Configure lightbox mode elements.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {Object} img - The image data.
 * @param {string} reportUid - The report UID.
 * @param {number} customFlexGrow - The custom flex grow value.
 * @param {number} tooltipContentWidth - The tooltip content width.
 * @param {boolean} onePhotoOnly - Whether there's only one photo.
 * @returns {void}
 */
export function configureLightboxMode(
    shadowRoot,
    img,
    reportUid,
    customFlexGrow,
    tooltipContentWidth,
    onePhotoOnly,
) {
    const dataReportIcons = shadowRoot.querySelector('[data-report-icons]');
    const dataReportPdf = shadowRoot.querySelector('[data-report-pdf]');
    const imgLightboxReportCard = shadowRoot.querySelector(
        '[data-img-lightbox-report-card]',
    );
    const deleteLightboxIcon = shadowRoot.querySelector('[data-lightbox-delete-icon]');
    const lightboxDownloadIcon = shadowRoot.querySelector(
        '[data-lightbox-download-icon]',
    );
    const addLightboxIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');
    const editLightboxIcon = shadowRoot.querySelector('[data-lightbox-edit-icon]');

    if (dataReportIcons) dataReportIcons.style.display = 'none';
    if (dataReportPdf) {
        dataReportPdf.style.display = 'none';
        dataReportPdf.style.opacity = 0;
    }
    if (imgLightboxReportCard) imgLightboxReportCard.style.display = 'flex';

    if (customFlexGrow <= MAX_FLEX_GROW_VALUE || img.img_width === 'auto') {
        if (imgLightboxReportCard) {
            imgLightboxReportCard.style.width = '40px';
            imgLightboxReportCard.style.height = 'fit-content';
            imgLightboxReportCard.style.maxHeight = '90%';
            imgLightboxReportCard.style.flexDirection = 'column';
        }

        setTimeout(() => {
            [
                deleteLightboxIcon,
                lightboxDownloadIcon,
                addLightboxIcon,
                editLightboxIcon,
            ].forEach((element) => {
                if (element) {
                    element.setAttribute(
                        TOOLTIP_POSITION_ATTRIBUTE,
                        TOOLTIP_POSITION.TOP_RIGHT,
                    );
                }
            });
        }, 0);
    } else {
        setTimeout(() => {
            [
                deleteLightboxIcon,
                lightboxDownloadIcon,
                addLightboxIcon,
                editLightboxIcon,
            ].forEach((element) => {
                if (element) {
                    element.setAttribute(
                        TOOLTIP_POSITION_ATTRIBUTE,
                        TOOLTIP_POSITION.BOTTOM_LEFT,
                    );
                }
            });
        }, 0);
    }

    setTimeout(() => {
        [deleteLightboxIcon, lightboxDownloadIcon, addLightboxIcon].forEach((element) => {
            if (element) {
                element.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
                element.setAttribute(
                    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                    tooltipContentWidth,
                );
            }
        });

        [deleteLightboxIcon, addLightboxIcon, editLightboxIcon].forEach((element) => {
            if (element) {
                element.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
                element.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
                element.setAttribute(
                    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                    tooltipContentWidth,
                );
            }
        });

        if (deleteLightboxIcon) {
            deleteLightboxIcon.setAttribute(
                LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
                REMOVE_ACTIVE_ITEM.LIGHTBOX_COLLECTION,
            );
        }

        if (lightboxDownloadIcon) {
            lightboxDownloadIcon.setAttribute(
                ACTIVE_ID_ATTRIBUTE,
                onePhotoOnly ? img.uid : reportUid,
            );
            lightboxDownloadIcon.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                onePhotoOnly
                    ? ADD_CART_ELEMENT_TYPE.ELEMENT
                    : ADD_CART_ELEMENT_TYPE.REPORT,
            );
        }

        if (addLightboxIcon) {
            addLightboxIcon.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                ADD_CART_ELEMENT_TYPE.LIGHTBOX,
            );
            addLightboxIcon.setAttribute(
                DETAILS_VIEW_ATTRIBUTE_IMG,
                JSON.stringify({
                    img_src: img.img_src,
                    caption: img.caption,
                }),
            );
            addLightboxIcon.setAttribute(
                LIGHTBOX_MODAL_ATTRIBUTES.ELEMENT_TYPE,
                ELEMENT_TYPE_ADD_LIGHTBOX.LIGHTBOX,
            );
        }

        if (editLightboxIcon) {
            editLightboxIcon.setAttribute(ITEM_NAME_ATTRIBUTE, reportUid);
        }
    }, 0);
}

/**
 * Configure normal mode elements.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {Object} img - The image data.
 * @param {string} reportUid - The report UID.
 * @param {number} customFlexGrow - The custom flex grow value.
 * @param {number} tooltipContentWidth - The tooltip content width.
 * @param {boolean} hasPdf - Whether the report has PDF.
 * @returns {void}
 */
export function configureNormalMode(
    shadowRoot,
    img,
    reportUid,
    customFlexGrow,
    tooltipContentWidth,
    hasPdf,
) {
    const dataReportPdf = shadowRoot.querySelector('[data-report-pdf]');
    const dataReportIcons = shadowRoot.querySelector('[data-report-icons]');

    if (!hasPdf) {
        if (dataReportPdf) {
            dataReportPdf.style.display = 'none';
            dataReportPdf.style.opacity = 0;
        }
        if (dataReportIcons) dataReportIcons.style.top = '30px';
    }

    const dataReportAddCart = shadowRoot.querySelector('[data-report-add-cart]');
    const imgLightboxIcon = shadowRoot.querySelector('[data-add-report-icon]');
    const pdfIconComponent = shadowRoot.querySelector('[data-report-pdf-icon]');
    const dataReportDownload = shadowRoot.querySelector('[data-report-download-icon]');

    setTimeout(() => {
        [
            dataReportAddCart,
            dataReportDownload,
            imgLightboxIcon,
            pdfIconComponent,
        ].forEach((element) => {
            if (element) {
                element.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
                element.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
                element.setAttribute(
                    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                    tooltipContentWidth,
                );
                element.setAttribute(
                    ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                    ADD_CART_ELEMENT_TYPE.REPORT,
                );
                element.setAttribute(
                    TOOLTIP_POSITION_ATTRIBUTE,
                    TOOLTIP_POSITION.TOP_LEFT,
                );
            }
        });

        if (imgLightboxIcon) {
            imgLightboxIcon.setAttribute(
                DETAILS_VIEW_ATTRIBUTE_IMG,
                JSON.stringify({
                    img_src: img.img_src,
                    caption: img.caption,
                }),
            );
            imgLightboxIcon.setAttribute(BOUND_POSITION_ATTRIBUTE, BOUND_POSITIONS.LEFT);
            imgLightboxIcon.setAttribute(
                LIGHTBOX_MODAL_ATTRIBUTES.ELEMENT_TYPE,
                ELEMENT_TYPE_ADD_LIGHTBOX.REPORT,
            );
        }
    }, 0);

    if (isCreativeReportType()) {
        const titleElement = shadowRoot.querySelector('h3');
        if (titleElement) titleElement.style.display = 'none';
    }
}

/**
 * Setup report type display.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {string} currentReportType - The current report type.
 * @param {string} typeReport - The type report.
 * @returns {void}
 */
export function setupReportType(shadowRoot, currentReportType, typeReport) {
    if (
        currentReportType &&
        (COMMON_REPORTS.includes(currentReportType) ||
            currentReportType.includes('All')) &&
        !VIDEO_REPORTS.includes(typeReport)
    ) {
        const typeReportElement = shadowRoot.querySelector('[data-type-report]');
        if (typeReportElement) {
            typeReportElement.style.setProperty('display', 'flex', 'important');
            const typeTextElement = typeReportElement.querySelector('p');
            if (typeTextElement) {
                typeTextElement.innerText = translate(`reportCard.${typeReport}`);
            }
        }
    }
}

/**
 * Setup video configuration.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @param {Object} img - The image data.
 * @param {string} reportUid - The report UID.
 * @param {number} customFlexGrow - The custom flex grow value.
 * @param {number} tooltipContentWidth - The tooltip content width.
 * @param {boolean} onePhotoOnly - Whether there's only one photo.
 * @returns {void}
 */
export function setupVideoConfiguration(
    shadowRoot,
    img,
    reportUid,
    customFlexGrow,
    tooltipContentWidth,
    onePhotoOnly,
) {
    if (!isVideoItem(img.img_zoom)) return;

    const reportIconCard = shadowRoot.querySelector('[data-report-icon-card]');
    if (reportIconCard) {
        reportIconCard.innerHTML = 'videocam';
    }

    if (isReportVideosList()) {
        const zoomModalIcon = shadowRoot.querySelector('[data-zoom-modal-icon]');
        if (zoomModalIcon) {
            zoomModalIcon.style.display = 'flex';

            setTimeout(() => {
                zoomModalIcon.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
                zoomModalIcon.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
                zoomModalIcon.setAttribute(
                    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                    tooltipContentWidth,
                );

                if (onePhotoOnly) {
                    const dataReportDownload = shadowRoot.querySelector(
                        '[data-report-download-icon]',
                    );
                    if (dataReportDownload) {
                        dataReportDownload.setAttribute(
                            ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                            ADD_CART_ELEMENT_TYPE.ELEMENT,
                        );
                        dataReportDownload.setAttribute(ACTIVE_ID_ATTRIBUTE, img.uid);
                    }
                }
            }, 0);
        }
    }
}

/**
 * Add exclusive watermark to the image.
 * @param {HTMLDivElement} mainContainer - The main container element.
 * @param {HTMLImageElement} imgElement - The image element.
 * @param {Object} img - The image data.
 * @returns {void}
 */
export function addExclusiveWatermark(mainContainer, imgElement, img) {
    if (!img.isExclusive) return;

    const exclusiveImg = document.createElement('img');
    exclusiveImg.src = `assets/img/exclusive_watermark_${
        getStoreValue(WESOG_STORE_PARAMS.CURRENT_LANGUAGE) || DEFAULT_LANGUAGE
    }.svg`;
    exclusiveImg.alt = 'Exclusive watermark';
    exclusiveImg.classList.add(COMMON_EXCLUSIVE_WATERMARK_IMG);

    mainContainer.insertBefore(exclusiveImg, imgElement);
}

/**
 * Setup zoom configuration for small zoom values.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @returns {void}
 */
export function setupZoomConfiguration(shadowRoot) {
    const zoomConfigValue =
        getStoreConfiguration()[WESOG_STORE_CONFIGURATION_PARAMS.ZOOM];

    if (zoomConfigValue < DEFAULT_ZOOM_VALUE) {
        const imgIconsReportAddCarts = shadowRoot.querySelectorAll(
            '.img-icons-report-add-cart',
        );

        imgIconsReportAddCarts.forEach((element) => {
            element.style.maxWidth = '100%';
            element.style.flexDirection = 'row';
            element.style.justifyContent = 'flex-end';
        });
    }
}

/**
 * Get video URL with caching support.
 * @param {string} videoUrl - The video URL.
 * @returns {Promise<string>} The cached video URL.
 */
export async function getVideoUrl(videoUrl) {
    const cache = await caches.open(SERVICE_WORKER_VERSION);
    const cachedResponse = await cache.match(videoUrl);

    if (cachedResponse) {
        return URL.createObjectURL(await cachedResponse.blob());
    }

    const response = await loadVideoWithPartialContentSupport(fetch, videoUrl);
    const responseClone = response.clone();
    const videoBlobUrl = URL.createObjectURL(await response.blob());

    await cache.put(videoUrl, responseClone);
    return videoBlobUrl;
}

/**
 * Show the loading spinner.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @returns {void}
 */
export function showLoadingSpinner(shadowRoot) {
    const spinner = shadowRoot.querySelector(
        CUSTOM_REPORT_CARD_LOADING_SPINNER_WEB_COMPONENT,
    );
    if (spinner) {
        spinner.style.display = 'flex';
    }
}

/**
 * Hide the loading spinner.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component.
 * @returns {void}
 */
export function hideLoadingSpinner(shadowRoot) {
    const spinner = shadowRoot.querySelector(
        CUSTOM_REPORT_CARD_LOADING_SPINNER_WEB_COMPONENT,
    );
    if (spinner) {
        spinner.style.display = 'none';
    }
}
